<script lang="ts">
  // Angular VRAINS button: top-left + bottom-right corners cut, neon edge + glow,
  // and a glossy highlight that tracks the cursor (premium card-shine). Full
  // default/hover/focus/active/disabled states. `magenta` variant for
  // destructive/opponent actions, `ghost` for secondary.
  import type { Snippet } from "svelte";

  let {
    children,
    onclick,
    variant = "cyan",
    type = "button",
    disabled = false,
    full = false,
    class: cls = "",
  }: {
    children: Snippet;
    onclick?: (e: MouseEvent) => void;
    variant?: "cyan" | "magenta" | "ghost";
    type?: "button" | "submit";
    disabled?: boolean;
    full?: boolean;
    class?: string;
  } = $props();

  let mx = $state(50);
  let my = $state(50);
  let hot = $state(false);

  function move(e: PointerEvent) {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    mx = ((e.clientX - r.left) / r.width) * 100;
    my = ((e.clientY - r.top) / r.height) * 100;
  }

  const edge = $derived(
    variant === "magenta" ? "bg-magenta/80" : variant === "ghost" ? "bg-cyan-dim/50" : "bg-cyan/70",
  );
  const glow = $derived(variant === "magenta" ? "hover:glow-magenta" : "hover:glow-cyan");
  const shineColor = $derived(variant === "magenta" ? "255,90,160" : "150,225,255");
</script>

<button
  {type}
  {disabled}
  {onclick}
  onpointermove={move}
  onpointerenter={() => (hot = true)}
  onpointerleave={() => (hot = false)}
  class="tp-btn group chamfer-btn relative p-px transition-[filter,transform] duration-200 ease-[var(--ease-out-quint)] disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:brightness-110 enabled:active:scale-[0.985] enabled:focus-visible:brightness-125 {edge} {glow} {variant ===
  'magenta'
    ? 'enabled:focus-visible:glow-magenta'
    : 'enabled:focus-visible:glow-cyan'} {full ? 'w-full' : ''} {cls}"
  style="--c:10px; --mx:{mx}%; --my:{my}%; --shine:{shineColor}"
>
  <span
    class="chamfer-btn relative flex items-center justify-center gap-2 overflow-hidden bg-surface-2 px-6 py-2.5 font-display text-sm font-semibold uppercase tracking-[0.12em] text-ink transition-colors duration-200 group-hover:text-glow group-active:bg-surface"
    style="--c:9px"
  >
    <!-- Cursor-tracking gloss + a thin top sheen. -->
    <span
      class="pointer-events-none absolute inset-0 transition-opacity duration-200"
      style="opacity:{hot ? 1 : 0}; background: radial-gradient(110px circle at var(--mx) var(--my), rgba(var(--shine),0.40), rgba(var(--shine),0.10) 35%, transparent 62%)"
    ></span>
    <span
      class="pointer-events-none absolute inset-x-0 top-0 h-1/2 opacity-50"
      style="background: linear-gradient(rgba(255,255,255,0.10), transparent)"
    ></span>
    <!-- One quick scanline sweep on hover (see DESIGN.md motion spec). -->
    <span class="scan pointer-events-none absolute inset-y-0 left-0 w-1/3" aria-hidden="true"></span>
    <span class="relative z-10">{@render children()}</span>
  </span>
</button>

<style>
  .scan {
    opacity: 0;
    background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.32), transparent);
    transform: translateX(-140%) skewX(-18deg);
  }
  button:enabled:hover .scan {
    animation: btn-scan 0.55s var(--ease-out-quint);
  }
  @keyframes btn-scan {
    from {
      opacity: 1;
      transform: translateX(-140%) skewX(-18deg);
    }
    to {
      opacity: 1;
      transform: translateX(440%) skewX(-18deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    button:enabled:hover .scan {
      animation: none;
    }
  }
</style>
